import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Pencil, User, Eye } from "lucide-react";
import { toast } from "sonner";
import PhotoUpload, { toAuthUrl } from "@/components/PhotoUpload";
import PlayerDetailDialog from "@/components/PlayerDetailDialog";

const CATEGORIES = ["PRO", "AMATEUR", "U17", "U13"];
const CATEGORY_LABELS = {
  PRO: "Senior Pro Dames",
  AMATEUR: "Senior Amateur Dames",
  U17: "U17",
  U13: "U13",
};

const STATUS_COLORS = {
  active: "bg-emerald-100 text-emerald-700 border-emerald-200",
  injured: "bg-red-100 text-red-700 border-red-200",
  suspended: "bg-amber-100 text-amber-700 border-amber-200",
  inactive: "bg-slate-100 text-slate-700 border-slate-200",
};

const STATUS_LABELS = {
  active: "Active",
  injured: "Blessée",
  suspended: "Suspendue",
  inactive: "Inactive",
};

const EMPTY = {
  first_name: "", last_name: "", category: "PRO", position: "",
  date_of_birth: "", nationality: "Bénin", height_cm: "", weight_kg: "",
  license_number: "", previous_club: "", jersey_number: "", photo_url: "",
  coach_observations: "", status: "active",
};

export default function Equipes() {
  const [players, setPlayers] = useState([]);
  const [tab, setTab] = useState("PRO");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [detailId, setDetailId] = useState(null);

  const load = () => api.get("/players").then((r) => setPlayers(r.data));
  useEffect(() => { load(); }, []);

  const filtered = players.filter((p) => p.category === tab);

  const startCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY, category: tab });
    setOpen(true);
  };
  const startEdit = (p) => {
    setEditing(p);
    setForm({ ...EMPTY, ...p });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        height_cm: form.height_cm ? Number(form.height_cm) : null,
        weight_kg: form.weight_kg ? Number(form.weight_kg) : null,
        jersey_number: form.jersey_number ? Number(form.jersey_number) : null,
      };
      if (editing) {
        await api.patch(`/players/${editing.id}`, payload);
        toast.success("Joueuse mise à jour");
      } else {
        await api.post("/players", payload);
        toast.success("Joueuse ajoutée");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cette joueuse ?")) return;
    try {
      await api.delete(`/players/${id}`);
      toast.success("Supprimée");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="equipes-page">
      <PageHeader title="Gestion sportive" subtitle="Effectif complet du club par catégorie">
        <Button onClick={startCreate} data-testid="add-player-button" className="bg-emerald-600 hover:bg-emerald-700">
          <Plus className="h-4 w-4 mr-2" /> Nouvelle joueuse
        </Button>
      </PageHeader>

      <div className="p-6 lg:p-8">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="category-tabs" className="bg-white border border-slate-200">
            {CATEGORIES.map((c) => (
              <TabsTrigger key={c} value={c} data-testid={`tab-${c}`}
                className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white font-semibold uppercase tracking-wider text-xs">
                {CATEGORY_LABELS[c]} ({players.filter((p) => p.category === c).length})
              </TabsTrigger>
            ))}
          </TabsList>

          {CATEGORIES.map((c) => (
            <TabsContent key={c} value={c} className="mt-6">
              {filtered.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-md p-12 text-center text-slate-500">
                  Aucune joueuse dans cette catégorie.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {filtered.map((p) => (
                    <div key={p.id} data-testid={`player-card-${p.id}`}
                      className="stat-card bg-white border border-slate-200 rounded-md overflow-hidden">
                      <div className="h-40 bg-gradient-to-br from-emerald-50 to-slate-100 flex items-center justify-center relative cursor-pointer"
                        onClick={() => setDetailId(p.id)}>
                        {p.photo_url ? (
                          <img src={toAuthUrl(p.photo_url)} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <User className="h-16 w-16 text-emerald-300" />
                        )}
                        <Badge className={`absolute top-2 right-2 ${STATUS_COLORS[p.status] || STATUS_COLORS.active}`}>
                          {STATUS_LABELS[p.status] || p.status}
                        </Badge>
                        {p.jersey_number != null && (
                          <div className="absolute top-2 left-2 bg-white/90 backdrop-blur-sm h-9 w-9 rounded-md flex items-center justify-center font-barlow font-bold text-lg text-emerald-700">
                            {p.jersey_number}
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <div className="font-barlow font-bold text-lg tracking-tight text-slate-900 truncate">
                          {p.first_name} {p.last_name}
                        </div>
                        <div className="text-xs text-slate-500 mb-3">{p.position || "—"} • {p.nationality}</div>

                        <div className="grid grid-cols-3 gap-1 mb-3 border-t border-slate-100 pt-3">
                          <Stat label="Pts" value={p.points} />
                          <Stat label="Passes" value={p.assists} />
                          <Stat label="Rebonds" value={p.rebounds} />
                        </div>

                        <div className="text-[10px] text-slate-400 uppercase tracking-wider truncate">
                          Licence : {p.license_number || "—"}
                        </div>

                        <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
                          <Button size="sm" variant="outline" onClick={() => setDetailId(p.id)}
                            data-testid={`view-player-${p.id}`} className="flex-1">
                            <Eye className="h-3 w-3 mr-1" /> Détails
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => startEdit(p)}
                            data-testid={`edit-player-${p.id}`} className="h-8 w-8 p-0">
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => del(p.id)}
                            data-testid={`delete-player-${p.id}`}
                            className="text-red-600 hover:bg-red-50 border-red-200 h-8 w-8 p-0">
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="player-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow text-2xl">
              {editing ? "Modifier la joueuse" : "Ajouter une joueuse"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Prénom" value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} required testid="player-firstname" />
            <Field label="Nom" value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} required testid="player-lastname" />
            <SelectField label="Catégorie" value={form.category} onChange={(v) => setForm({ ...form, category: v })}
              options={CATEGORIES.map((c) => ({ value: c, label: CATEGORY_LABELS[c] }))} testid="player-category" />
            <Field label="Poste" value={form.position} onChange={(v) => setForm({ ...form, position: v })} testid="player-position" />
            <Field label="Date de naissance" type="date" value={form.date_of_birth} onChange={(v) => setForm({ ...form, date_of_birth: v })} testid="player-dob" />
            <Field label="Nationalité" value={form.nationality} onChange={(v) => setForm({ ...form, nationality: v })} testid="player-nationality" />
            <Field label="Taille (cm)" type="number" value={form.height_cm} onChange={(v) => setForm({ ...form, height_cm: v })} testid="player-height" />
            <Field label="Poids (kg)" type="number" value={form.weight_kg} onChange={(v) => setForm({ ...form, weight_kg: v })} testid="player-weight" />
            <Field label="N° Licence" value={form.license_number} onChange={(v) => setForm({ ...form, license_number: v })} testid="player-license" />
            <Field label="N° Maillot" type="number" value={form.jersey_number} onChange={(v) => setForm({ ...form, jersey_number: v })} testid="player-jersey" />
            <Field label="Club précédent" value={form.previous_club} onChange={(v) => setForm({ ...form, previous_club: v })} testid="player-prevclub" />
            <SelectField label="Statut" value={form.status} onChange={(v) => setForm({ ...form, status: v })}
              options={[
                { value: "active", label: "Active" },
                { value: "injured", label: "Blessée" },
                { value: "suspended", label: "Suspendue" },
                { value: "inactive", label: "Inactive" },
              ]} testid="player-status" />
            <div className="col-span-2">
              <PhotoUpload value={form.photo_url}
                onChange={(v) => setForm({ ...form, photo_url: v })}
                testid="player-photo" label="Photo de la joueuse" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-1 block">
                Observations de l'entraîneur
              </Label>
              <Textarea data-testid="player-observations" value={form.coach_observations || ""}
                onChange={(e) => setForm({ ...form, coach_observations: e.target.value })}
                placeholder="Progression, points forts, axes d'amélioration..." rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} data-testid="save-player-button" className="bg-emerald-600 hover:bg-emerald-700">
              {editing ? "Mettre à jour" : "Créer la fiche"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PlayerDetailDialog playerId={detailId} open={!!detailId}
        onClose={() => setDetailId(null)} onChanged={load} />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-barlow font-bold text-base text-slate-900 leading-none">{value ?? 0}</div>
      <div className="text-[9px] uppercase tracking-wider text-slate-400">{label}</div>
    </div>
  );
}

export function Field({ label, value, onChange, type = "text", required, testid, placeholder }) {
  return (
    <div>
      <Label className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-1 block">
        {label}{required && <span className="text-red-500"> *</span>}
      </Label>
      <Input data-testid={testid} type={type} value={value ?? ""} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} required={required} />
    </div>
  );
}

export function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-1 block">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
